import * as THREE from 'three';
import { CONFIG } from '../../config.js';
import { NavigationGraph } from '../../npc/NavigationGraph.js';
import { BinaryMaterial } from '../../rendering/binary/BinaryMaterial.js';
import { getSharedBinaryAtlas } from '../../rendering/binary/sharedBinaryResources.js';
import { DigitalSign } from '../city/DigitalSign.js';
import { chunkKey, chunkCenterWorld, chunkChebyshevDist, worldToChunkCoord } from './worldGrid.js';
import { WorldChunk } from './WorldChunk.js';
import { BuildingEntranceRegistry } from '../interiors/BuildingEntranceRegistry.js';

/**
 * @typedef {{
 *   take: () => number;
 *   report: (cellsUsed: number, maxColumns: number, drawCalls?: number) => void;
 * }} CellAllocator
 */

/**
 * @typedef {{
 *   buildingMaterial: BinaryMaterial;
 *   accentMaterial: BinaryMaterial;
 *   accentPoleMaterial: BinaryMaterial;
 *   cityConfig: typeof CONFIG.city;
 *   chunkWorldSize: number;
 *   alloc: CellAllocator;
 *   signs: DigitalSign[];
 *   entranceRegistry: BuildingEntranceRegistry;
 * }} ChunkBuildContext
 */

/**
 * Loads / unloads {@link WorldChunk} tiles around the player.
 */
export class ChunkManager {
  /**
   * @param {typeof CONFIG.city} cityConfig
   * @param {typeof CONFIG.streaming} streamingConfig
   */
  constructor(cityConfig, streamingConfig) {
    this.cityConfig = cityConfig;
    this.streaming = streamingConfig;

    this.group = new THREE.Group();
    this.group.name = 'StreamingCity';

    /** @type {Map<string, WorldChunk>} */
    this._chunks = new Map();
    this.colliders = [];
    this.navigationGraph = new NavigationGraph();

    /** @type {DigitalSign[]} */
    this._signs = [];
    this.entranceRegistry = new BuildingEntranceRegistry();

    this._cellIdNext = 0;
    this._maxColumns = 8;
    this._drawCalls = 0;

    this._atlas = getSharedBinaryAtlas();
    this._buildingMaterial = new BinaryMaterial({
      atlas: this._atlas,
      palette: 'building',
    });
    this._accentMaterial = new BinaryMaterial({
      atlas: this._atlas,
      palette: 'accent',
      overrides: {
        glowStrength: CONFIG.luminance.accentGlowStrength,
        flickerStrength: 0.1,
      },
    });
    this._accentPoleMaterial = new BinaryMaterial({
      atlas: this._atlas,
      palette: 'accent',
      overrides: {
        glowStrength: CONFIG.luminance.poleGlowStrength,
        flickerStrength: 0.06,
      },
    });

    /** @type {CellAllocator} */
    this._alloc = {
      take: () => this._cellIdNext,
      report: (cellsUsed, maxColumns, drawCalls = 0) => {
        this._cellIdNext += cellsUsed;
        this._maxColumns = Math.max(this._maxColumns, maxColumns);
        this._drawCalls += drawCalls;
      },
    };

    /** @type {ChunkBuildContext} */
    this._ctx = {
      buildingMaterial: this._buildingMaterial,
      accentMaterial: this._accentMaterial,
      accentPoleMaterial: this._accentPoleMaterial,
      cityConfig,
      chunkWorldSize: streamingConfig.chunkSize,
      alloc: this._alloc,
      signs: this._signs,
      entranceRegistry: this.entranceRegistry,
    };

    this._lastPlayerChunk = { cx: NaN, cz: NaN };
    this._navDirty = true;

    /** @type {{ chunkX: number; chunkZ: number }[]} */
    this._loadQueue = [];
    this._colliderCache = [];
    this._colliderCacheCx = NaN;
    this._colliderCacheCz = NaN;
  }

  bootstrap() {
    this.update(0, 0, 0, true);
  }

  getSpawnPoint() {
    return { x: 6, y: 0, z: -10 };
  }

  /**
   * @param {number} playerX
   * @param {number} playerZ
   * @param {number} deltaSeconds
   * @param {boolean} [force]
   */
  update(playerX, playerZ, deltaSeconds, force = false) {
    const {
      chunkSize,
      renderDistance,
      unloadPadding,
      maxChunksPerFrame = 2,
    } = this.streaming;
    const { cx, cz } = worldToChunkCoord(playerX, playerZ, chunkSize);
    const loadRadius = Math.ceil(renderDistance / chunkSize);
    const unloadRadius = loadRadius + unloadPadding;
    const chunkHalf = chunkSize * 0.5;

    const playerChunkChanged =
      force ||
      cx !== this._lastPlayerChunk.cx ||
      cz !== this._lastPlayerChunk.cz;

    if (playerChunkChanged) {
      this._lastPlayerChunk = { cx, cz };
      this._invalidateColliderCache();
    }

    /** @type {Set<string>} */
    const needed = new Set();
    for (let dz = -loadRadius; dz <= loadRadius; dz += 1) {
      for (let dx = -loadRadius; dx <= loadRadius; dx += 1) {
        const tx = cx + dx;
        const tz = cz + dz;
        const center = chunkCenterWorld(tx, tz, chunkSize);
        const dist = Math.hypot(center.x - playerX, center.z - playerZ);
        if (dist > renderDistance + chunkHalf) continue;

        const key = chunkKey(tx, tz);
        needed.add(key);
        if (!this._chunks.has(key) && !this._isQueued(tx, tz)) {
          const ring = chunkChebyshevDist(tx, tz, cx, cz);
          if (force && ring <= 1) {
            this._loadChunk(tx, tz, cx, cz);
          } else {
            this._loadQueue.push({ chunkX: tx, chunkZ: tz });
          }
        }
      }
    }

    let budget = force ? Math.max(maxChunksPerFrame, 12) : maxChunksPerFrame;
    while (budget > 0 && this._loadQueue.length > 0) {
      const next = this._loadQueue.shift();
      if (!next) break;
      const key = chunkKey(next.chunkX, next.chunkZ);
      if (!needed.has(key) || this._chunks.has(key)) continue;
      this._loadChunk(next.chunkX, next.chunkZ, cx, cz);
      budget -= 1;
    }

    this._loadQueue = this._loadQueue.filter((item) =>
      needed.has(chunkKey(item.chunkX, item.chunkZ)),
    );

    for (const [key, chunk] of this._chunks) {
      const dist = chunkChebyshevDist(chunk.chunkX, chunk.chunkZ, cx, cz);
      if (dist > unloadRadius || !needed.has(key)) {
        this._unloadChunk(key);
      }
    }

    if (this._navDirty) {
      this._rebuildNavigation();
    }

    this._buildingMaterial.setColumns(this._maxColumns);
    this._accentMaterial.setColumns(this._maxColumns);
    this._accentPoleMaterial.setColumns(this._maxColumns);
    this._tick(deltaSeconds, playerX, playerZ);
  }

  /**
   * Colliders for player movement — nearby chunks only.
   * @param {number} playerX
   * @param {number} playerZ
   */
  getCollidersNear(playerX, playerZ) {
    const { chunkSize, collisionChunkRadius = 1 } = this.streaming;
    const { cx, cz } = worldToChunkCoord(playerX, playerZ, chunkSize);
    if (cx === this._colliderCacheCx && cz === this._colliderCacheCz) {
      return this._colliderCache;
    }

    const list = [];
    const r = collisionChunkRadius;
    for (const chunk of this._chunks.values()) {
      if (
        Math.abs(chunk.chunkX - cx) <= r &&
        Math.abs(chunk.chunkZ - cz) <= r
      ) {
        list.push(...chunk.colliders);
      }
    }
    this._colliderCache = list;
    this._colliderCacheCx = cx;
    this._colliderCacheCz = cz;
    return list;
  }

  _invalidateColliderCache() {
    this._colliderCacheCx = NaN;
    this._colliderCacheCz = NaN;
    this._colliderCache = [];
  }

  /** @param {number} chunkX @param {number} chunkZ */
  _isQueued(chunkX, chunkZ) {
    return this._loadQueue.some(
      (q) => q.chunkX === chunkX && q.chunkZ === chunkZ,
    );
  }

  dispose() {
    for (const chunk of this._chunks.values()) {
      chunk.dispose();
      this.group.remove(chunk.group);
    }
    this._chunks.clear();
    for (const sign of this._signs) sign.dispose();
    this._signs = [];
    this._buildingMaterial.dispose();
    this._accentMaterial.dispose();
    this._accentPoleMaterial.dispose();
    this.colliders = [];
    this.navigationGraph = new NavigationGraph();
  }

  _loadChunk(chunkX, chunkZ, playerCx, playerCz) {
    const key = chunkKey(chunkX, chunkZ);
    const ring = chunkChebyshevDist(chunkX, chunkZ, playerCx, playerCz);
    const lod = ring <= 0 ? 0 : ring <= 1 ? 1 : 2;
    const chunk = new WorldChunk(chunkX, chunkZ, this._ctx, lod);
    chunk.build();
    this._chunks.set(key, chunk);
    this.group.add(chunk.group);
    this._navDirty = true;
    this._syncColliders();
    this._invalidateColliderCache();
  }

  _unloadChunk(key) {
    const chunk = this._chunks.get(key);
    if (!chunk) return;
    this.entranceRegistry.unregisterChunk(key);
    chunk.dispose();
    this.group.remove(chunk.group);
    this._chunks.delete(key);
    this._navDirty = true;
    this._syncColliders();
    this._invalidateColliderCache();
  }

  _syncColliders() {
    this.colliders = [];
    for (const chunk of this._chunks.values()) {
      this.colliders.push(...chunk.colliders);
    }
  }

  _rebuildNavigation() {
    this.navigationGraph = new NavigationGraph();
    const spacing = CONFIG.npc.navNodeSpacing;
    for (const chunk of this._chunks.values()) {
      this.navigationGraph.appendRegion(
        this.cityConfig,
        chunk.bounds,
        spacing,
      );
    }
    this.navigationGraph.reconnectNeighbors(6.5);
    this._navDirty = false;
  }

  getStreamingStats() {
    let buildingsLoaded = 0;
    /** @type {{ minX: number; maxX: number; minZ: number; maxZ: number }[]} */
    const chunkBounds = [];
    for (const chunk of this._chunks.values()) {
      buildingsLoaded += chunk.buildingCount ?? 0;
      chunkBounds.push(chunk.bounds);
    }
    return {
      activeChunks: this._chunks.size,
      buildingsLoaded,
      chunkBounds,
    };
  }

  _tick(deltaSeconds, playerX, playerZ) {
    if (!CONFIG.graphics?.staticBinaryGlyphs) {
      this._buildingMaterial.update(deltaSeconds);
      this._accentMaterial.update(deltaSeconds);
      this._accentPoleMaterial.update(deltaSeconds);
    }

    const tickDist =
      (CONFIG.streaming?.simulationDistance ?? 90) + 24;
    const tickDistSq = tickDist * tickDist;
    const signFlicker = CONFIG.graphics?.signFlicker !== false;
    const signPos = _signCullPos;

    for (const sign of this._signs) {
      if (!signFlicker) continue;
      sign.mesh.getWorldPosition(signPos);
      const dx = signPos.x - playerX;
      const dz = signPos.z - playerZ;
      if (dx * dx + dz * dz > tickDistSq) continue;
      sign.update(deltaSeconds);
    }
  }

  /** @param {number} boost */
  setEventBoost(boost) {
    this._buildingMaterial.setEventBoost(boost);
    this._accentMaterial.setEventBoost(boost * 0.65);
    this._accentPoleMaterial.setEventBoost(boost * 0.4);
  }

  /** @param {number} strength */
  setAccentFlicker(strength) {
    this._accentMaterial.setFlickerStrength(strength);
  }
}

const _signCullPos = new THREE.Vector3();
